import { ParameterDefinition } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Button, IconButton, Popover } from '@mui/material';
import { Stack } from '@mui/system';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useToggle } from 'usehooks-ts';

import { ExposingParameterMenuItem } from './ExposingParameterMenuItem';
import {
  selectParameterDefinitions,
  selectTaskPropertiesStrict,
} from '../../../../features/workflow/selectors';
import {
  updateConditionAppliedParameter,
  updateTaskAppliedParameters,
} from '../../../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { NewParameterReferenceModal } from '../../../GraphEditorToolbars/ParametersToolbar/NewParameterReferenceModal';
import { ParameterType } from '../../../GraphEditorToolbars/ParametersToolbar/types';
import {
  createParameter,
  getParameterType,
} from '../../../GraphEditorToolbars/ParametersToolbar/utils';

export type ExposingParameterPopoverProps = {
  appliedParameterId?: string;
  parameterName: string;
  padding?: number;
  taskId: string;
  types: ParameterType | ParameterType[];
};

export const ExposingParameterPopover = ({
  appliedParameterId,
  parameterName,
  taskId,
  padding = 6,
  types,
}: ExposingParameterPopoverProps) => {
  const parameter = createParameter(Array.isArray(types) ? types[0]! : types);
  const parameters = useAppSelector(selectParameterDefinitions);
  const task = useAppSelector(selectTaskPropertiesStrict)[taskId];
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [parameterIdToApply, setParameterIdToApply] = useState<string | null>(
    null
  );
  const [showNewParameterReferenceModal, handleNewParameterReferenceModal] =
    useToggle();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget),
    []
  );
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleApplyParameter = useCallback(
    (param: ParameterDefinition) => {
      if (task?.propertiesType === 'condition') {
        dispatch(
          updateConditionAppliedParameter({
            taskId,
            parameterName,
            parameterId: param.id,
          })
        );
      } else {
        dispatch(
          updateTaskAppliedParameters({
            taskId,
            parameterName,
            parameterId: param.id,
          })
        );
      }
      handleClose();
    },
    [parameterName, task?.propertiesType, taskId, dispatch, handleClose]
  );

  const handleCreateAndUpdateParameter = useCallback(
    (paramId?: string) => {
      handleNewParameterReferenceModal();
      if (typeof paramId === 'string') {
        setParameterIdToApply(paramId);
      }
    },
    [handleNewParameterReferenceModal]
  );

  const filteredParameters = parameters.filter((param) => {
    const parameterType = getParameterType(param);

    return (
      parameterType === types ||
      (Array.isArray(types) && types.includes(parameterType))
    );
  });
  useEffect(() => {
    if (parameterIdToApply) {
      const newParameter = parameters.find(
        (param) => param.id === parameterIdToApply
      );

      if (!newParameter) return;

      handleApplyParameter(newParameter);
      setParameterIdToApply(null);
    }
  }, [parameterIdToApply, parameters, handleApplyParameter]);

  return (
    <Stack
      paddingBottom={`${padding}px`}
      direction="row"
      alignItems="center"
      gap={1}
    >
      <IconButton
        size="small"
        aria-label="info popover icon"
        onClick={handleClick}
      >
        <TuneIcon fontSize="inherit" />
      </IconButton>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box padding={2}>
          <Button variant="outlined" onClick={handleNewParameterReferenceModal}>
            New Parameter Reference
          </Button>
          <Stack direction="column">
            <Stack minWidth={400} marginTop={2}>
              {filteredParameters.length > 0 ? (
                filteredParameters.map((param) => (
                  <ExposingParameterMenuItem
                    isSelected={appliedParameterId === param.id}
                    key={param.id}
                    parameter={param}
                    onClick={() => handleApplyParameter(param)}
                  />
                ))
              ) : (
                <Box textAlign="center" padding={2}>
                  No parameters available for this type.
                </Box>
              )}
            </Stack>
          </Stack>
        </Box>
      </Popover>
      {showNewParameterReferenceModal && (
        <NewParameterReferenceModal
          isTypeFixed={true}
          parameter={parameter}
          typeOptions={Array.isArray(types) ? types : [types]}
          onClose={handleCreateAndUpdateParameter}
        />
      )}
    </Stack>
  );
};
